import { environment } from './../../../environments/environment';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { CountriesService } from '../../global/services/countries.service';
import { Country } from '../../global/interfaces/country';
import { CountryIso } from '../../global/interfaces/countryIso';
import { PreferredLocation } from '../../global/interfaces/prefferedLocation';
import { RegionPrefix } from '../types/RegionPrefix';
import { ClientLocation } from '../types/ClientLocation';
import { InnerLocation } from '../types/InnerLocation';
import { objectToQueryParams } from '../../utils/query.functions';
import { Gates } from '../types/Gates';
import { Pagination } from '../types/Pagination';
@Injectable({
  providedIn: 'root',
})
export class LocationsService {
  prefix = `${environment.serverUrl}/shipping-locations`;
  DEFAULT_COUNTRY_ISO = 'GB';
  DEFAULT_COUNTRY_ISO3 = 'GBR';
  DEFAULT_COUNTRY_PHONECODE = 44;

  constructor(private http: HttpClient, private countriesService: CountriesService) {}

  async getShippingLocations(params?: {
    search?: string;
    clientIds?: string[];
    allowNullClientId?: boolean;
    perPage?: number;
    currentPage?: number;
  }): Promise<PreferredLocation[]> {
    return await this.http.get<PreferredLocation[]>(`${this.prefix}?${objectToQueryParams(params)}`).toPromise();
  }

  async getShippingLocationById(locationId: string): Promise<PreferredLocation> {
    return await this.http.get<PreferredLocation>(`${this.prefix}/${locationId}`).toPromise();
  }

  async addClientLocation(location: ClientLocation) {
    return await this.http.post(`${this.prefix}/client-locations`, location).toPromise();
  }

  async addShipperLocation(location: Partial<PreferredLocation>) {
    return await this.http.post(`${this.prefix}/shipper-locations`, location).toPromise();
  }

  async getClientLocations(params?: {
    search?: string;
    clientIds?: string[];
    countryIso3?: string;
    perPage?: number;
    currentPage?: number;
  }): Promise<ClientLocation[]> {
    return await this.http
      .get<ClientLocation[]>(`${this.prefix}/client-locations?${objectToQueryParams(params)}`)
      .toPromise();
  }

  async getClientLocation(locationId: string): Promise<ClientLocation> {
    return await this.http.get<ClientLocation>(`${this.prefix}/client-locations/${locationId}`).toPromise();
  }

  async editClientLocation(locationId: string, location: ClientLocation) {
    return await this.http.put(`${this.prefix}/client-locations/${locationId}`, location).toPromise();
  }

  async getInnerLocation(locationId: string) {
    return await this.http.get<InnerLocation>(`${this.prefix}/inner-locations/${locationId}`).toPromise();
  }

  async getInnerLocations(params?: { search?: string; countryIso3?: string; perPage?: number; currentPage?: number }) {
    return await this.http
      .get<InnerLocation[]>(`${this.prefix}/inner-locations?${objectToQueryParams(params)}`)
      .toPromise();
  }

  async addInnerLocation(location: InnerLocation) {
    return await this.http.post(`${this.prefix}/inner-locations`, location).toPromise();
  }

  async editInnerLocation(location: InnerLocation) {
    return await this.http.put(`${this.prefix}/inner-locations/${location.id}`, location).toPromise();
  }

  async deleteLocations(locationIds: string[]) {
    if (!locationIds.length) return;
    const queryParams = locationIds.map((location, index) => {
      return `ids[${index}]=${location}`;
    });
    return await this.http.delete(`${this.prefix}?${queryParams.join('&')}`).toPromise();
  }

  async getPosition(): Promise<any> {
    return new Promise((res, rej) => {
      navigator.geolocation.getCurrentPosition(res, rej);
    });
  }
  async findIsoCountry(countries: Country[]) {
    const countryIso: CountryIso = {
      countryIso: this.DEFAULT_COUNTRY_ISO,
      countryIso3: this.DEFAULT_COUNTRY_ISO3,
      countryCode: this.DEFAULT_COUNTRY_PHONECODE,
    };
    const coords = JSON.parse(localStorage.getItem('userCoords'));
    const iso3 = await this.getIso3FromCoordiantes(coords);
    if (iso3) {
      countryIso.countryIso3 = iso3;
    }
    countries.forEach((country) => {
      if (country.iso3 === countryIso.countryIso3) {
        countryIso.countryIso = country.iso;
        countryIso.countryCode = country.phonecode;
        localStorage.setItem('countryIso', JSON.stringify(countryIso));
      }
    });
    return countryIso;
  }
  async getIso3FromCoordiantes(coords) {
    if (coords?.latitude && coords?.longitude) {
      try {
        const address = await this.countriesService.getFullAddress(coords);
        return address?.iso3;
      } catch {
        return null;
      }
    }
    return null;
  }
  getRegionPrefixes(): Promise<RegionPrefix[]> {
    const url = '/region-prefix';
    return this.http.get<RegionPrefix[]>(this.prefix + url).toPromise();
  }

  async getGatesByLocation(location: InnerLocation) {
    return await this.http.get<Gates[]>(`${this.prefix}/inner-locations/${location}/gates`).toPromise();
  }
}
